import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { SkeletonRows, ErrorMessage, usePageMeta } from '../components/ui';

const pts = (x) => (x == null ? '—' : `${x >= 0 ? '+' : ''}${(x * 100).toFixed(1)}`);
const pct = (x) => (x == null ? '—' : `${(x * 100).toFixed(0)}%`);

/* The four corners of the chart, in the order the legend filter lists them. */
const QUADS = [
  { key: 'meta + skill', label: 'Meta + skill', color: 'var(--lime)', blurb: 'Wins at the top and gets better with skill' },
  { key: 'meta, easy', label: 'Meta, easy', color: 'var(--down)', blurb: 'Wins at the top, but its edge shrinks with skill' },
  { key: 'skill, off-meta', label: 'Skill, off-meta', color: 'var(--tier-b)', blurb: 'Rewards skill, not winning at the top yet' },
  { key: 'weak', label: 'Neither', color: 'var(--faint)', blurb: 'No clear edge either way' },
];
const QUAD = Object.fromEntries(QUADS.map((q) => [q.key, q]));

const BRACKET_LABEL = { '<7k': '<7k', '7k-10k': '7k–10k', '10k+': '10k+', PoL: 'Path of Legends' };

function CardArt({ card }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="av card-art">
      {card.icon && !broken ? (
        <img src={card.icon} alt={card.name} loading="lazy" onError={() => setBroken(true)} />
      ) : (
        <span className="av-fb">{card.name.charAt(0)}</span>
      )}
    </span>
  );
}

/* Elite strength (x) against skill slope (y). No labels by default: with a
   hundred-odd cards they pile on top of each other. Hovering a dot, or its
   row in the legend, lifts it, names it and dims the rest. */
const W = 640;
const H = 460;
const PAD = 40;

function Scatter({ cards, active, setActive, pinned, setPinned }) {
  const scale = useMemo(() => {
    const xs = cards.map((c) => c.meta);
    const ys = cards.map((c) => c.skill);
    const xr = Math.max(0.005, ...xs.map(Math.abs)) * 1.15;
    const yr = Math.max(0.003, ...ys.map(Math.abs)) * 1.15;
    return {
      x: (v) => PAD + ((v + xr) / (2 * xr)) * (W - 2 * PAD),
      y: (v) => H - PAD - ((v + yr) / (2 * yr)) * (H - 2 * PAD),
    };
  }, [cards]);

  const focus = active ?? pinned;
  const hot = cards.find((c) => c.id === focus);
  const x0 = scale.x(0);
  const y0 = scale.y(0);
  const r = (c) => 3.5 + Math.min(c.elite_use, 0.4) * 22;

  // Label sits right of the dot, or left of it near the right edge.
  let label = null;
  if (hot) {
    const cx = scale.x(hot.meta);
    const cy = scale.y(hot.skill);
    const narrow = typeof window !== 'undefined' && window.innerWidth <= 860;
    const w = hot.name.length * (narrow ? 12 : 7.4) + 16;
    const h = narrow ? 34 : 26;
    const left = cx + r(hot) + 6 + w > W - 4;
    const lx = left ? cx - r(hot) - 6 - w : cx + r(hot) + 6;
    label = (
      <g className="sc-label" pointerEvents="none">
        <rect x={lx} y={cy - h / 2} width={w} height={h} rx={7} />
        <text x={lx + 8} y={cy + h / 6}>{hot.name}</text>
      </g>
    );
  }

  return (
    <svg className="scatter" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label="Each card's elite strength against how much it gains with skill"
      onMouseLeave={() => setActive(null)}>
      <rect className="sc-hot" x={x0} y={PAD} width={W - PAD - x0} height={y0 - PAD} />
      <line className="sc-axis" x1={PAD} x2={W - PAD} y1={y0} y2={y0} />
      <line className="sc-axis" x1={x0} x2={x0} y1={PAD} y2={H - PAD} />
      <text className="sc-q" x={W - PAD} y={PAD - 12} textAnchor="end">Meta + skill</text>
      <text className="sc-q" x={PAD} y={PAD - 12}>Skill, off-meta</text>
      <text className="sc-q" x={W - PAD} y={H - PAD + 22} textAnchor="end">Meta, easy</text>
      <text className="sc-q" x={PAD} y={H - PAD + 22}>Neither</text>
      <text className="sc-ax" x={W / 2} y={H - 4} textAnchor="middle">Stronger in elite games →</text>
      <text className="sc-ax" x={12} y={H / 2} textAnchor="middle" transform={`rotate(-90 12 ${H / 2})`}>
        Gains more with skill →
      </text>
      {cards.map((c) => (
        <circle
          key={c.id}
          cx={scale.x(c.meta)}
          cy={scale.y(c.skill)}
          r={r(c)}
          fill={QUAD[c.quadrant]?.color}
          className={`sc-dot${focus == null ? '' : c.id === focus ? ' on' : ' dim'}`}
          onMouseEnter={() => setActive(c.id)}
          onClick={() => setPinned((p) => (p === c.id ? null : c.id))}
        />
      ))}
      {/* Drawn again on top so the focused dot is never hidden under a neighbour. */}
      {hot && (
        <circle cx={scale.x(hot.meta)} cy={scale.y(hot.skill)} r={r(hot)}
          fill={QUAD[hot.quadrant]?.color} className="sc-dot on" pointerEvents="none" />
      )}
      {label}
    </svg>
  );
}

function Legend({ cards, active, setActive, pinned, setPinned }) {
  const box = useRef(null);
  const focus = active ?? pinned;

  // Hovering a dot scrolls its row into view inside the legend, without
  // moving the page (scrollIntoView would scroll the window too).
  useEffect(() => {
    if (focus == null || !box.current) return;
    const row = box.current.querySelector(`[data-id="${focus}"]`);
    if (!row) return;
    const top = row.offsetTop - box.current.offsetTop;
    const { scrollTop, clientHeight } = box.current;
    if (top < scrollTop || top + row.offsetHeight > scrollTop + clientHeight) {
      box.current.scrollTo({ top: top - clientHeight / 2 + row.offsetHeight / 2, behavior: 'smooth' });
    }
  }, [focus]);

  return (
    <ol className="sc-legend" ref={box} onMouseLeave={() => setActive(null)}>
      {cards.map((c) => (
        <li key={c.id} data-id={c.id}
          className={c.id === focus ? 'on' : ''}
          onMouseEnter={() => setActive(c.id)}
          onClick={() => setPinned((p) => (p === c.id ? null : c.id))}>
          <span className="lg-rank">{c.rank}</span>
          <span className="lg-dot" style={{ background: QUAD[c.quadrant]?.color }} />
          <span className="lg-name">{c.name}</span>
          <span className="lg-num">{pts(c.meta)} / {pts(c.skill)}</span>
        </li>
      ))}
    </ol>
  );
}

/* Which Clash Royale cards are meta, and which of those take skill. Every card
   gets two level-adjusted numbers from tracked battles: its win rate in elite
   games, and how that win rate changes from low Trophy Road up to Path of
   Legends. Built weekly by scripts/clash_skill.py. */
function ClashMeta() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  const [active, setActive] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [shown, setShown] = useState(() => new Set(QUADS.map((q) => q.key)));
  const [sort, setSort] = useState({ key: 'rank', dir: 1 });
  usePageMeta({
    title: 'Clash Royale Meta Cards That Take Skill',
    description: 'Which Clash Royale cards win at the top, and which of them get better the better the players are: every card scored from tracked battles and refreshed weekly.',
  });

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    api.getClashMeta()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [nonce]);

  const cards = useMemo(() => (data?.cards || []).map((c, i) => ({ ...c, rank: i + 1 })), [data]);
  const visible = useMemo(() => cards.filter((c) => shown.has(c.quadrant)), [cards, shown]);
  const counts = useMemo(() => {
    const n = {};
    cards.forEach((c) => { n[c.quadrant] = (n[c.quadrant] || 0) + 1; });
    return n;
  }, [cards]);

  const rows = useMemo(() => visible.slice().sort((a, b) => {
    const av = a[sort.key];
    const bv = b[sort.key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string') return av.localeCompare(bv) * sort.dir;
    return (av - bv) * sort.dir;
  }), [visible, sort]);

  const toggle = (key) => setShown((s) => {
    const next = new Set(s);
    if (next.has(key) && next.size > 1) next.delete(key);
    else next.add(key);
    return next;
  });

  const th = (key, label, title, first = -1) => (
    <th className="sortable" title={title}
      onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : first }))}>
      {label}
      {sort.key === key && <span className="arr">{sort.dir < 0 ? '▼' : '▲'}</span>}
    </th>
  );

  const top = cards.find((c) => c.quadrant === 'meta + skill');
  const easy = cards.filter((c) => c.quadrant === 'meta, easy').sort((a, b) => b.meta - a.meta).slice(0, 3);
  const brackets = data?.brackets || [];

  return (
    <div className="page">
      <div className="wrap">
        <div className="phead">
          <div>
            <div className="eyebrow">Clash Royale · updated weekly</div>
            <h1>Meta Cards That Take Skill</h1>
            <div className="sub">
              Which cards win at the top, and which of those get better the better the players are,
              measured from {data ? `${(data.ladder_battles + data.ranked_battles).toLocaleString()} ` : ''}real
              battles{data ? ` over the last ${data.window_days} days` : ''}.
            </div>
          </div>
        </div>

        {error ? (
          <ErrorMessage message={error} onRetry={() => setNonce((n) => n + 1)} />
        ) : !data ? (
          <SkeletonRows rows={8} />
        ) : (
          <>
            <div className="stat-ribbon">
              <div className="srib"><div className="v">{top ? top.name : '—'}</div><div className="l">Top meta + skill card</div></div>
              <div className="srib"><div className="v">{top ? `${pts(top.meta)} pts` : '—'}</div><div className="l">Above par in elite games</div></div>
              <div className="srib"><div className="v">{top ? `${pts(top.skill)} pts` : '—'}</div><div className="l">Gained per skill bracket</div></div>
              <div className="srib"><div className="v">{data.generated_at.slice(0, 10)}</div><div className="l">Last refreshed</div></div>
            </div>

            <div className="sc-card">
              <div className="sc-filters">
                {QUADS.map((q) => (
                  <button key={q.key} title={q.blurb}
                    className={`sc-chip${shown.has(q.key) ? ' on' : ''}`}
                    onClick={() => toggle(q.key)}>
                    <span className="lg-dot" style={{ background: q.color }} />
                    {q.label} <span className="sc-n">{counts[q.key] || 0}</span>
                  </button>
                ))}
              </div>
              <div className="sc-body">
                <Scatter cards={visible} active={active} setActive={setActive} pinned={pinned} setPinned={setPinned} />
                <div className="sc-side">
                  <div className="sc-side-h"><span>Card</span><span>Elite / skill pts</span></div>
                  <Legend cards={visible} active={active} setActive={setActive} pinned={pinned} setPinned={setPinned} />
                </div>
              </div>
              <div className="sc-note">
                Hover or tap a dot or a name to find a card. Dot size is how often it's played in elite decks.
              </div>
            </div>

            {easy.length > 0 && (
              <div className="tip beat">
                <b>Strong but easy to answer at the top:</b> {easy.map((c) => c.name).join(', ')}. These win
                in elite games, but their edge shrinks as the players get better.
              </div>
            )}

            <div className="tablecard">
              <div className="table-scroll">
                <table className="meta">
                  <thead>
                    <tr>
                      {th('rank', '#', 'Ranked by the weaker of the two scores, so a card has to be strong on both', 1)}
                      {th('name', 'Card', null, 1)}
                      {th('meta', 'Elite', 'Adjusted win rate in 10k+ Trophy Road and Path of Legends, in points above 50%')}
                      {th('skill', 'Skill', 'Change in adjusted win rate per bracket, from <7k up to Path of Legends')}
                      {th('elite_use', 'Played', 'Share of elite decks running this card')}
                      {brackets.map((b) => (
                        <th key={b} title={`Adjusted win rate vs 50% in ${BRACKET_LABEL[b] || b}`}>{BRACKET_LABEL[b] || b}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => (
                      <tr key={c.id} className={c.id === (active ?? pinned) ? 'row-on' : ''}
                        onMouseEnter={() => setActive(c.id)} onMouseLeave={() => setActive(null)}>
                        <td className="rank">{c.rank}</td>
                        <td style={{ minWidth: 200 }}>
                          <div className="pcell">
                            <CardArt card={c} />
                            <div>
                              <div className="pn" style={{ whiteSpace: 'nowrap' }}>{c.name}</div>
                              <div className="pt">
                                <span className="lg-dot" style={{ background: QUAD[c.quadrant]?.color }} />{' '}
                                {QUAD[c.quadrant]?.label} · {c.elite_plays.toLocaleString()} elite games
                              </div>
                            </div>
                          </div>
                        </td>
                        <td><span className={`trend ${c.meta > 0 ? 'up' : c.meta < 0 ? 'down' : 'flat'}`}>{pts(c.meta)}</span></td>
                        <td><span className={`trend ${c.skill > 0 ? 'up' : c.skill < 0 ? 'down' : 'flat'}`}>{pts(c.skill)}</span></td>
                        <td><span className="num-cell">{pct(c.elite_use)}</span></td>
                        {brackets.map((b) => {
                          const s = c.brackets?.[b];
                          return (
                            <td key={b}><span className="num-cell">
                              {s && s.n >= data.min_bracket_plays ? pts(s.adj_win_rate - 0.5) : '—'}
                            </span></td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="tip">
              <b>How to read this.</b> <b>Elite</b> is how many points above a 50% win rate the card scores in
              10k+ Trophy Road and Path of Legends, after removing what the card-level matchup alone predicts.{' '}
              <b>Skill</b> is how much that win rate climbs per bracket from under 7k trophies up to Path of
              Legends. Matchmaking pairs players of similar trophies, so 50% is par in every bracket: a card
              that keeps gaining as the opponents get better rewards the player using it. A card that wins a
              lot low down and fades at the top is strong mainly because lower-bracket opponents don't answer
              it well. Cards with few games are pulled toward zero, a card needs {data.min_elite_plays} elite
              games to be scored, and mirror matches are left out. Players choose their cards, so a rising
              slope is partly the kind of player who picks it.
            </div>
            <div className="tip">
              Found your card? <Link to="/clash/decks">See decks built around it →</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ClashMeta;
